import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import api, { apiErrorMessage } from "../lib/api";
import { REPORT_IMPACT, REPORT_KIND, REPORT_STATUS, SCREENSHOT_WARNING } from "../lib/portalTickets";
import { Chip, fieldCls, primaryBtn, secondaryBtn, Shell } from "../components/portal/PortalShell";
import ScreenshotPicker, { uploadScreenshots } from "../components/admin/tickets/ScreenshotPicker";
import { Thumbnails } from "../components/admin/tickets/TicketScreenshots";

function when(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString("ro-RO", { dateStyle: "short", timeStyle: "short" });
}

/**
 * `/portal/raportari/:ticketId` — one report (14.10.2026): what was sent, the
 * conversation with "Echipa FPED", an answer, and — once a fix is live —
 * "Da, merge" or "Tot nu merge". The link in every portal ticket email.
 */
export default function PortalReport() {
  const { ticketId } = useParams();
  const [t, setT] = useState(null);
  const [shots, setShots] = useState([]);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const [one, pics] = await Promise.all([
        api.get(`/portal/tickets/${ticketId}`),
        api.get(`/portal/tickets/${ticketId}/attachments`).catch(() => ({ data: { items: [] } })),
      ]);
      setT(one.data);
      setShots(pics.data.items || []);
      setError("");
    } catch (err) {
      setError(err?.response?.status === 401 ? "out" : apiErrorMessage(err, "Nu am găsit raportarea."));
    }
  }, [ticketId]);

  useEffect(() => { load(); }, [load]);

  return (
    <Shell wide report={false}>
      <Link to="/portal/raportari" className="inline-flex items-center gap-1.5 text-sm text-[#6B7280] hover:text-[#965EA7]">
        <ArrowLeft size={14} /> Raportările mele
      </Link>
      {error === "out" && (
        <p className="mt-8 text-sm text-[#373A45]">
          Sesiunea s-a încheiat. <Link to="/portal" className="font-semibold text-[#965EA7]">Intră din nou</Link>, apoi deschide
          raportarea din email sau din „Raportările mele”.
        </p>
      )}
      {error && error !== "out" && <p className="mt-8 text-sm text-[#c0483f]" role="alert">{error}</p>}
      {!t && !error && <div className="py-24 text-center text-[#6B7280]"><Loader2 className="mx-auto animate-spin" /></div>}
      {t && <Body t={t} shots={shots} reload={load} />}
    </Shell>
  );
}

function Body({ t, shots, reload }) {
  const own = shots.filter((s) => !s.comment_id);
  return (
    <article className="mt-6" data-testid="portal-report">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-xs font-semibold text-[#7A4986]">{t.label}</span>
        <Chip tone={REPORT_STATUS[t.status]?.tone}>{REPORT_STATUS[t.status]?.label || t.status}</Chip>
      </div>
      <h1 className="mt-2 font-display text-3xl font-bold tracking-tight text-[#373A45] [text-wrap:balance]">{t.title}</h1>
      <p className="mt-1 text-sm text-[#6B7280]">
        {REPORT_KIND[t.kind]?.label} · {REPORT_IMPACT[t.impact]} · trimisă {when(t.created_at)}
      </p>

      {t.status === "rejected" && t.reject_reason && (
        <p className="mt-5 rounded-2xl border border-[#E5E7EB] bg-white p-4 text-sm text-[#373A45]">
          <strong>Am închis raportarea fără o modificare în portal:</strong> {t.reject_reason}
        </p>
      )}
      {t.status === "needs_info" && (
        <p className="mt-5 rounded-2xl border border-[#d99a2b]/40 bg-[#d99a2b]/10 p-4 text-sm text-[#373A45]" data-testid="portal-report-needs-info">
          Avem nevoie de câteva detalii. Răspunde aici și ne uităm din nou.
        </p>
      )}
      {["to_verify", "resolved"].includes(t.status) && <Verify t={t} reload={reload} />}

      <div className="[overflow-wrap:anywhere] mt-6 whitespace-pre-line rounded-2xl border border-[#E5E7EB] bg-white p-5 text-[15px] leading-relaxed text-[#373A45]">
        {t.description}
        {t.steps && <><br /><br /><span className="text-sm text-[#6B7280]">Ce ai făcut înainte:</span><br />{t.steps}</>}
      </div>
      {own.length > 0 && <Thumbnails items={own} className="mt-4" />}

      <Conversation t={t} shots={shots} reload={reload} />
    </article>
  );
}

function Verify({ t, reload }) {
  const [writing, setWriting] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const waiting = t.status === "to_verify";

  const act = async (path, body, done) => {
    setBusy(true);
    try {
      await api.post(`/portal/tickets/${t.id}/${path}`, body);
      toast.success(done);
      await reload();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Nu a mers. Încearcă din nou."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className={`mt-5 rounded-2xl border p-5 ${waiting ? "border-[#519872]/40 bg-[#519872]/[0.07]" : "border-[#E5E7EB] bg-white"}`}
      data-testid="portal-report-verify">
      {waiting ? (
        <>
          <p className="font-semibold text-[#3c7457]">Am rezolvat. Merge acum?</p>
          <p className="mt-1 text-sm text-[#373A45]">Dacă nu ne spui în 7 zile, considerăm că problema e rezolvată.</p>
        </>
      ) : (
        <p className="text-sm text-[#373A45]">Raportarea e rezolvată. Dacă problema a apărut din nou, o poți redeschide.</p>
      )}
      {writing ? (
        <div className="mt-3 space-y-2">
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} maxLength={5000} autoFocus
            placeholder="Ce nu merge încă?" className={fieldCls} />
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setWriting(false)} disabled={busy} className={secondaryBtn}>Renunță</button>
            <button type="button" disabled={busy || !text.trim()} className={primaryBtn}
              onClick={() => act("reopen", { text }, "Am redeschis raportarea și am primit mesajul tău.")}>
              {busy && <Loader2 size={16} className="animate-spin" />} Redeschide raportarea
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-3 flex flex-wrap gap-2">
          {waiting && (
            <button type="button" disabled={busy} className={primaryBtn} data-testid="portal-report-confirm"
              onClick={() => act("confirm", {}, "Mulțumim! Raportarea e rezolvată.")}>
              Da, merge
            </button>
          )}
          <button type="button" disabled={busy} className={secondaryBtn} onClick={() => setWriting(true)}>Tot nu merge</button>
        </div>
      )}
    </section>
  );
}

function Conversation({ t, shots, reload }) {
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const box = useRef(null);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      const r = await api.post(`/portal/tickets/${t.id}/comments`, { text });
      if (files.length) await uploadScreenshots(t.id, files, { commentId: r.data.comment.id, base: "/portal/tickets" });
      toast.success("Mesajul a fost trimis.");
      setText("");
      setFiles([]);
      await reload();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Mesajul nu a putut fi trimis."));
    } finally {
      setBusy(false);
    }
  };

  const closed = ["resolved", "rejected", "duplicate"].includes(t.status);
  return (
    <section ref={box} className="mt-8" data-testid="portal-report-conversation">
      <h2 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#6B7280]">Discuție</h2>
      {(t.comments || []).length === 0 ? (
        <p className="mt-3 text-sm text-[#6B7280]">Încă nu a scris nimeni aici.</p>
      ) : (
        <ol className="mt-3 grid gap-3">
          {t.comments.map((c) => (
            <li key={c.id} className={`rounded-2xl px-4 py-3 ${c.mine ? "ml-8 bg-[#965EA7]/[0.07]" : "mr-8 border border-[#E5E7EB] bg-white"}`}>
              <div className="flex items-baseline justify-between gap-3 text-xs">
                <span className="font-semibold text-[#373A45]">{c.mine ? "Tu" : c.name}</span>
                <span className="text-[#6B7280]">{when(c.created_at)}</span>
              </div>
              <p className="[overflow-wrap:anywhere] mt-1 whitespace-pre-wrap text-sm leading-relaxed text-[#373A45]">{c.text}</p>
              {shots.some((s) => s.comment_id === c.id) && (
                <Thumbnails items={shots.filter((s) => s.comment_id === c.id)} small className="mt-2" />
              )}
            </li>
          ))}
        </ol>
      )}
      {!closed && (
        <form onSubmit={send} className="mt-4 space-y-2">
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} maxLength={5000}
            placeholder="Scrie un mesaj…" className={fieldCls} data-testid="portal-report-message" />
          <p className="text-xs text-[#8a5d12]">{SCREENSHOT_WARNING}</p>
          <ScreenshotPicker files={files} onChange={setFiles} max={3} pasteTarget={box} />
          <div className="flex justify-end">
            <button type="submit" disabled={busy || !text.trim()} className={primaryBtn} data-testid="portal-report-message-send">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Trimite mesajul
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
